import '@/styles/book-storefront.css'

import { BookLanguageSwitcher } from '@/components/booking/BookLanguageSwitcher'

/**
 * Mobile storefront shell — matches dining-app glow-studio layout (max-w-md column).
 *
 * @param {{
 *   branding?: { theme_primary?: string, theme_secondary?: string, theme_accent?: string },
 *   outerClassName?: string,
 *   innerClassName?: string,
 *   children: import('react').ReactNode,
 * }} props
 */
export function PublicBookShell({
  branding = {},
  outerClassName = 'bg-[#F1EAD9]',
  innerClassName = 'bg-[#FBF6EC]',
  children,
}) {
  const style = {
    '--book-primary': branding.theme_primary ?? '#334155',
    '--book-secondary': branding.theme_secondary ?? '#DB2777',
    '--book-accent': branding.theme_accent ?? '#22C55E',
  }

  return (
    <div className={`book-storefront min-h-[100dvh] flex justify-center ${outerClassName}`} style={style}>
      <div
        className={`relative w-full max-w-md min-h-[100dvh] border border-white/60 shadow-[0_36px_95px_-22px_rgba(50,30,15,0.36)] ${innerClassName}`}
      >
        <div className="absolute end-3 top-3 z-20">
          <BookLanguageSwitcher />
        </div>
        {children}
      </div>
    </div>
  )
}

/**
 * @param {{ url?: string | null, name: string, alt?: string, className?: string }} props
 */
export function BookLogo({ url, name, alt, className = '' }) {
  if (url) {
    return (
      <img
        src={url}
        alt={alt ?? `${name} logo`}
        className={`object-contain ${className}`}
        style={{ maxWidth: '9rem', maxHeight: '7rem', width: 'auto' }}
      />
    )
  }

  const initials = name
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

  return (
    <span className={`font-extrabold uppercase tracking-[0.2em] text-white ${className}`}>
      {initials}
    </span>
  )
}

/**
 * @param {{ links?: Array<{ key: string, label: string, url: string }> }} props
 */
export function BookSocialLinks({ links = [] }) {
  if (links.length === 0) {
    return null
  }

  return (
    <div className="flex shrink-0 items-center justify-center gap-5 px-2 pb-3">
      {links.map((social) => (
        <a
          key={social.key}
          href={social.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={social.label}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-[0_10px_22px_-12px_rgba(0,0,0,0.45)] transition-transform hover:scale-105"
        >
          {social.key === 'whatsapp' ? (
            <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="#25D366"
                d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"
              />
              <path
                fill="#25D366"
                d="M12 0C5.373 0 0 5.373 0 12c0 2.11.547 4.09 1.507 5.82L0 24l6.335-1.662A11.93 11.93 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.82a9.79 9.79 0 0 1-4.986-1.364l-.357-.213-3.76.986 1.003-3.66-.233-.375A9.82 9.82 0 0 1 2.18 12C2.18 6.58 6.58 2.18 12 2.18S21.82 6.58 21.82 12 17.42 21.82 12 21.82z"
              />
            </svg>
          ) : social.key === 'instagram' ? (
            <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
              <defs>
                <linearGradient id={`ig-${social.key}`} x1="0%" y1="100%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#F58529" />
                  <stop offset="50%" stopColor="#DD2A7B" />
                  <stop offset="100%" stopColor="#8134AF" />
                </linearGradient>
              </defs>
              <path
                fill={`url(#ig-${social.key})`}
                d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm0 2a3 3 0 0 0-3 3v10a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H7zm5 3.5A5.5 5.5 0 1 1 6.5 13 5.5 5.5 0 0 1 12 7.5zm0 2A3.5 3.5 0 1 0 15.5 13 3.5 3.5 0 0 0 12 9.5zm5.75-6.1a1.25 1.25 0 1 1-1.25 1.25 1.25 1.25 0 0 1 1.25-1.25z"
              />
            </svg>
          ) : (
            <span className="text-xs font-bold">↗</span>
          )}
        </a>
      ))}
    </div>
  )
}
